import React, { useState } from "react";
import ReactMarkdown from 'react-markdown';
import DraggableContainer from "../shared/DraggableContainer.js";
import Selector from "../shared/Selector.js";
import NumericInput from "../shared/NumericInput.js";
import ResponsiveContainer from "../shared/ResponsiveContainer.js";
import ActionButton from "../shared/ActionButton.js";
import Message from "../shared/Message.js";
import Box from "../shared/Box.js";
import Timeline from '../Timeline/Timeline.js';
import SelectInput from "../shared/SelectInput.js";
import FeedbackWidget from '../shared/FeedbackWidget.js';
import { submitFeedback } from '../../services/feedbackService.js';

const OpportunityFormView = ({

  stage,

  commercialOptions,
  selectedCommercial,
  setSelectedCommercial,

  opportunityOptions,
  selectedOpportunity,
  setSelectedOpportunity,

  selectedModel,
  setSelectedModel,

  errorMessage,
  storyData,
  onGetTimeline,
  isLoadingStory,
  storyId,

  isUrlMode,
  opportunityId,

  maxNbToProcess,
  setMaxNbToProcess,
}) => {
  const [isParamsModalOpen, setIsParamsModalOpen] = useState(false);

  const handleClose = () => {
    setIsParamsModalOpen(false);
  };

  const handleFeedbackSubmit = async (feedbackData) => {
    try {
      await submitFeedback(feedbackData);
      // Optionally add success message or state update
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // Optionally handle error (show message, etc.)
    }
  };

  return (
    <>
    <DraggableContainer 
    title="Opportunity Explorer"
    initialPosition={{ top: 0, left: 345 }}
    initialWidth="calc(95% - 345px)"
    initialHeight="95vh"
    maxWidth="none"
    maxHeight="none"
    >

      {/* Params & Info Button */}
      {!isUrlMode &&  stage === 'staging' && (
        <ActionButton onClick={() => setIsParamsModalOpen(true)} text="Params & Info" />
      )}

      {/* Main Content - 2 columns */}
      <div className="grid grid-cols-2 gap-4 p-4 items-start">

        {/* First column */}
        <div className="grid grid-cols-1 gap-4 p-4">
         
          {/* Selector Section - Only show Commercial selector if not in URL mode */}
          {!isUrlMode && (
            <Selector
              label="Commercial"
              id="commercial"
              options={commercialOptions}
              value={selectedCommercial?.value}
              onChange={setSelectedCommercial}
            />
          )}
          <Selector
            label="Opportunity"
            id="opportunity"
            options={opportunityOptions}
            dependencies={[selectedCommercial]}
            value={selectedOpportunity?.value}
            onChange={setSelectedOpportunity}
            disabled={!selectedCommercial}
          />

          {/* Get Timeline */}
          <ActionButton onClick={onGetTimeline} text="Get Timeline" />

          {/* Loading/Error States */}
          {isLoadingStory ? (
            <div className="mt-4 p-4 border rounded-md bg-white">
              <div className="flex flex-col items-center">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-full rounded-full"
                  ></div>
                </div>
                <span className="mt-2 text-xs font-medium text-blue-600">
                  Loading...
                </span>
              </div>
            </div>
          ) : errorMessage ? (
            <div className="mt-4 p-4 border rounded-md bg-red-100 text-red-700">
              {errorMessage}
            </div>
          ) : (opportunityId && !opportunityOptions.find(o => o.id === opportunityId)) ? (
            <div className="mt-4 p-4 border rounded-md bg-white text-blue-600">
              Cette opportunité n'a pas encore suffisamment d'événements associés pour être traitée: {opportunityId}
            </div>
          ) : null}

          {storyData?.case_presentation && 
            (() => {
              let presentationData;
              if (typeof storyData.case_presentation === 'string') {
                try {
                  presentationData = JSON.parse(storyData.case_presentation);
                } catch (e) {
                  console.error("Failed to parse case_presentation JSON:", e);
                  // Fallback to rendering as string if parsing fails
                  return (
                    <Box>
                      <h4 className="text-sm font-medium text-gray-700 shrink-0">Présentation de l'opportunité</h4>
                      <div 
                        className="min-h-0 text-sm text-gray-600 pl-6 
                          whitespace-pre-wrap overflow-y-auto
                          scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
                      >
                        <ReactMarkdown>{storyData.case_presentation}</ReactMarkdown>
                      </div>
                      <FeedbackWidget
                          contentId={`opportunity-${selectedOpportunity?.value}`}
                          storyId={storyId}
                          contentType="case_presentation"
                          contentSection="case_presentation_raw"
                          onSubmitFeedback={handleFeedbackSubmit}
                          hasBeenSubmitted={false}
                      />
                    </Box>
                  );
                }
              } else if (typeof storyData.case_presentation === 'object') {
                presentationData = storyData.case_presentation;
              }

              if (!presentationData) return null;

              const fieldMappings = {
                project_overview: "Vue d'ensemble du projet",
                operational_summary: "Résumé opérationnel",
                needs_and_constraints: "Besoins et contraintes",
                next_steps: "Prochaines étapes",
                stakeholders: "Parties prenantes",
                advancements: "Avancements"
              };

              return (
                <Box>
                  <h4 className="text-sm font-medium text-gray-700 shrink-0 mb-2">Présentation de l'opportunité</h4>
                  {Object.entries(fieldMappings).map(([key, label]) => {
                    const value = presentationData[key];
                    if (value) { // Only render if the value exists
                      return (
                        <div key={key} className="mb-3">
                          <h5 className="text-xs font-semibold text-gray-600">{label}</h5>
                          {typeof value === 'string' ? (
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{value}</p>
                          ) : Array.isArray(value) ? (
                            <ul className="list-disc list-inside text-sm text-gray-800">
                              {value.map((item, index) => (
                                <li key={index}>{typeof item === 'object' ? JSON.stringify(item) : item}</li>
                              ))}
                            </ul>
                          ) : typeof value === 'object' ? (
                            <pre className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 p-2 rounded">
                              {JSON.stringify(value, null, 2)}
                            </pre>
                          ) : (
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{String(value)}</p>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })}
                  <FeedbackWidget
                      contentId={`opportunity-${selectedOpportunity?.value}`}
                      storyId={storyId}
                      contentType="case_presentation"
                      contentSection="case_presentation_structured"
                      onSubmitFeedback={handleFeedbackSubmit}
                      hasBeenSubmitted={false}
                  />
                </Box>
              );
            })()
          }
        </div>

        {/* Second column */}
        <div className="gap-4 p-4">
          {!selectedOpportunity?.id ? (
            <Message text="Sélectionnez une opportunité pour voir la timeline" />
          ) : (
            <Timeline events={storyData?.events || []} storyId={storyId} />
          )}
        </div>
        
      </div>

    </DraggableContainer>

    {isParamsModalOpen && (
      <DraggableContainer 
        title="Parameters & Information" 
        initialPosition={{ top: 0, left: 0 }}
        initialWidth="340px" 
        initialHeight="100vh"
        style={{ pointerEvents: 'auto' }}
        attachToViewport={true}
        onClose={handleClose}
      >
        <div className="p-4">
          <pre className="whitespace-pre-wrap word-wrap font-inherit">

              {/* Model selection */}
              <ResponsiveContainer
                label="Model selection"
              >
                <SelectInput
                  id="modelSelect"
                  value={selectedModel}
                  onChange={setSelectedModel}
                  options={[
                    { value: 'gpt-4o', label: 'gpt-4o' },
                    { value: 'gpt-4o-mini', label: 'gpt-4o-mini' },
                    { value: 'deepseek-chat', label: 'deepseek-chat' },
                  ]}
                />
              </ResponsiveContainer>

              {/* Check Box Section */}
              <ResponsiveContainer
                label="Processing parameters"
                columnsConfig={[
                  { minWidth: 0, columns: 1 },
                ]}
              >
                <NumericInput
                  label="#events to process - void=all"
                  id="maxNbToProcess"
                  value={maxNbToProcess === null ? '' : maxNbToProcess}
                  onChange={setMaxNbToProcess}
                  placeholder="Default: all"
                />
              </ResponsiveContainer>
              
              <Box>
                {/* Model info */}
                <span className="font-bold text-sm">Model info</span>
                <ul className="list-disc text-sm font-medium text-gray-700 pl-6 -mt-1">
                  <li>model: {storyData?.completion_usage?.model || ''}</li>
                  <li>completion: {storyData?.completion_usage?.object || ''}</li>
                </ul>

                <div className="border-t border-gray-200 my-2"></div>

                {/* Cost info */}
                <span className="font-bold text-sm">Cost info - US$ | tokens</span>
                <ul className="list-disc text-sm font-medium text-gray-700 pl-6 -mt-1">
                  <li>prompt:      {storyData?.completion_cost?.input_cost || ''} | {storyData?.completion_usage?.prompt_tokens || ''}</li>
                  <li>completion:  {storyData?.completion_cost?.output_cost || ''} | {storyData?.completion_usage?.completion_tokens || ''}</li>
                  <li>total:       {storyData?.completion_cost?.total_cost || ''} | {storyData?.completion_usage?.total_tokens || ''}</li>
                </ul>

                <div className="border-t border-gray-200 my-2"></div>
                
                {/* Processing time */}
                <span className="font-bold text-sm">Processing time</span>
                <ul className="list-disc text-sm font-medium text-gray-700 pl-6 -mt-1">
                  <li>{storyData?.completion_usage?.process_time ? `${storyData.completion_usage.process_time}s` : ''}</li>
                </ul>
              </Box>

          </pre>
        </div>
      </DraggableContainer>
    )}
    </>
  );
};

export default OpportunityFormView;